/**
 * The top-right corner bar: ways of getting around the floor, plus the two
 * controls that belong to the app (language, account).
 *
 * 'pre-enter' hides the floor-navigation buttons while the entry screen is
 * up - find-a-tool and Routes are ways around a floor the student is not
 * standing on yet. 'in-tool' hides them again over an open panel, where they
 * would sit across the assistant's header. Both used to be class-list pokes
 * from main.ts; now they are projections of the entered flag and the panel
 * store.
 */

import type { Journal } from '../../learn/Journal'
import type { Tool } from '../../fab/layout'
import type { Place } from '../../fab/places'
import { MISSIONS } from '../../learn/missions'
import { missionState } from '../../learn/missionState'
import { missionOnFloor } from '../../learn/floor'
import { dialogStore } from '../ui/dialogStore'
import { t, tOr } from '../../i18n/i18n'
import { useLang } from '../i18n/LanguageProvider'
import { useEntered, useHall, useMissionTick, useOpenTool } from '../state/hooks'
import { LanguageSwitch } from './LanguageSwitch'
import { AccountControl } from './AccountControl'

function FinderButton({ onJump }: { onJump: (target: Tool | Place) => void }) {
  useLang()
  return (
    <button
      className="route-open tf-open"
      title={t('finder.Placeholder')}
      onClick={() => dialogStore.open({ kind: 'finder', onJump })}
    >
      {t('finder.Button')}
    </button>
  )
}

function RoutesButton({ journal }: { journal: Journal }) {
  useLang()
  return (
    <button
      className="route-open"
      title={t('routes.ButtonTitle')}
      onClick={() => dialogStore.open({ kind: 'routes', journal })}
    >
      {t('routes.Button')}
    </button>
  )
}

function LineButton() {
  useLang()
  return (
    <button className="route-open" title={t('line.ButtonTitle')} onClick={() => dialogStore.open({ kind: 'line' })}>
      {t('line.Button')}
    </button>
  )
}

function WorkOrdersButton() {
  useLang()
  useMissionTick()
  // Over the orders this floor can honour (learn/floor.ts): a fab built
  // without an etcher does not owe its student the etcher's orders.
  useHall()
  const here = MISSIONS.filter(missionOnFloor)
  const total = here.length
  const done = here.filter((m) => missionState.isCompleted(m.id)).length
  return (
    <button className="route-open" onClick={() => dialogStore.open({ kind: 'orders' })}>
      {`${tOr('orders.Button', 'Work orders')} ${done}/${total}`}
    </button>
  )
}

export function TopRightCluster({
  jumpToTool,
  journal,
}: {
  jumpToTool: (target: Tool | Place) => void
  journal: Journal
}) {
  const entered = useEntered()
  const inTool = useOpenTool() !== null
  const cls = 'top-right' + (entered ? '' : ' pre-enter') + (inTool ? ' in-tool' : '')
  return (
    <div className={cls}>
      <FinderButton onJump={jumpToTool} />
      <RoutesButton journal={journal} />
      <LineButton />
      <WorkOrdersButton />
      <LanguageSwitch />
      <AccountControl />
    </div>
  )
}
